// Utility functions for loading and parsing legal documents

export const loadLegalDocument = async (documentType) => {
  try {
    const response = await fetch(`/legal/${documentType}.md`);
    if (!response.ok) {
      throw new Error(`Failed to load ${documentType}`);
    }
    const text = await response.text();
    return parseLegalDocument(text);
  } catch (error) {
    console.error(`Error loading legal document ${documentType}:`, error);
    return {
      title: 'Document Not Found',
      lastUpdated: new Date().toLocaleDateString(),
      sections: [
        {
          id: 'error',
          title: 'Error',
          content: 'Unable to load document content. Please try again later.'
        }
      ]
    };
  }
};

export const parseLegalDocument = (markdownText) => {
  const lines = markdownText.split('\n');
  const sections = [];
  let currentSection = null;
  let title = '';
  let lastUpdated = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Extract main title (first # heading)
    if (line.startsWith('# ') && !title) {
      title = line.substring(2).trim();
      continue;
    }

    // Extract last updated date
    if (line.includes('**Last updated:**')) {
      const match = line.match(/\*\*Last updated:\*\*\s*(.+)/);
      if (match) {
        lastUpdated = match[1].trim();
      }
      continue;
    }

    // Handle section headings (## headings)
    if (line.startsWith('## ')) {
      // Save previous section if exists
      if (currentSection) {
        sections.push(currentSection);
      }

      // Start new section
      const sectionTitle = line.substring(3).trim();
      currentSection = {
        id: generateSectionId(sectionTitle),
        title: sectionTitle,
        content: []
      };
      continue;
    }

    // Add content to current section
    if (currentSection && line) {
      currentSection.content.push(line);
    }
  }

  // Add the last section
  if (currentSection) {
    sections.push(currentSection);
  }

  return {
    title: title || 'Legal Document',
    lastUpdated: lastUpdated || new Date().toLocaleDateString(),
    sections: sections.map(section => ({
      ...section,
      content: section.content.join('\n')
    }))
  };
};

export const generateSectionId = (title) => {
  return title
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 20);
};

// Format markdown content for display
export const formatMarkdownContent = (content) => {
  if (!content) return '';

  const lines = content.split('\n');
  let html = '';
  let inList = false;
  let listDepth = 0;

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    const trimmedLine = line.trim();

    // Skip empty lines
    if (!trimmedLine) {
      if (inList) {
        html += '</ul>\n';
        inList = false;
        listDepth = 0;
      }
      html += '<br />\n';
      continue;
    }

    // Handle ### subheadings
    if (trimmedLine.startsWith('### ')) {
      if (inList) {
        html += '</ul>\n';
        inList = false;
        listDepth = 0;
      }
      const heading = trimmedLine.substring(4);
      html += `<h4 class="text-lg font-semibold text-gray-800 mt-4 mb-2">${heading}</h4>\n`;
      continue;
    }

    // Handle bullet points with various formats (-, •, *)
    const bulletMatch = trimmedLine.match(/^[-•*]\s+(.+)$/);
    if (bulletMatch) {
      if (!inList) {
        html += '<ul class="list-none space-y-2 ml-0">\n';
        inList = true;
      }
      const itemContent = formatInlineMarkdown(bulletMatch[1]);
      html += `<li class="flex items-start"><span class="text-orange-600 mr-2 mt-1">•</span><span class="flex-1">${itemContent}</span></li>\n`;
      continue;
    }

    // Handle numbered lists
    const numberMatch = trimmedLine.match(/^(\d+\.|\([a-z]\)|\*\*\d+\.\d+\*\*|\*\*[a-z]\)\*\*)\s+(.+)$/);
    if (numberMatch) {
      if (!inList) {
        html += '<ul class="list-none space-y-2 ml-0">\n';
        inList = true;
      }
      const prefix = numberMatch[1];
      const itemContent = formatInlineMarkdown(numberMatch[2]);
      html += `<li class="flex items-start"><span class="font-semibold text-gray-700 mr-2">${prefix}</span><span class="flex-1">${itemContent}</span></li>\n`;
      continue;
    }

    // Handle bold prefixed lines (e.g., "**2.1 Currency:**")
    const boldPrefixMatch = trimmedLine.match(/^\*\*([^*]+):\*\*\s*(.+)$/);
    if (boldPrefixMatch) {
      if (inList) {
        html += '</ul>\n';
        inList = false;
        listDepth = 0;
      }
      const label = boldPrefixMatch[1];
      const text = formatInlineMarkdown(boldPrefixMatch[2]);
      html += `<p class="mb-3"><strong class="font-semibold text-gray-800">${label}:</strong> ${text}</p>\n`;
      continue;
    }

    // Handle regular paragraphs
    if (inList) {
      html += '</ul>\n';
      inList = false;
      listDepth = 0;
    }

    const formatted = formatInlineMarkdown(trimmedLine);
    html += `<p class="mb-3 leading-relaxed">${formatted}</p>\n`;
  }

  // Close any open list
  if (inList) {
    html += '</ul>\n';
  }

  return html;
};

// Helper function to format inline markdown (bold, italic, links, etc.)
const formatInlineMarkdown = (text) => {
  if (!text) return '';

  // Convert bold text
  text = text.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>');

  // Convert italic text
  text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Convert inline code
  text = text.replace(/`(.+?)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm">$1</code>');

  // Convert links
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-orange-600 hover:text-orange-700 underline">$1</a>');

  return text;
};

// Get section icon based on content
export const getSectionIcon = (sectionTitle) => {
  const title = sectionTitle.toLowerCase();
  
  if (title.includes('introduction') || title.includes('acceptance')) return 'CheckCircle';
  if (title.includes('service') || title.includes('delivery')) return 'Users';
  if (title.includes('account') || title.includes('user')) return 'Shield';
  if (title.includes('conduct') || title.includes('prohibited')) return 'Scale';
  if (title.includes('privacy') || title.includes('data')) return 'FileText';
  if (title.includes('payment') || title.includes('fee')) return 'CreditCard';
  if (title.includes('termination') || title.includes('violation')) return 'AlertTriangle';
  if (title.includes('liability') || title.includes('limitation')) return 'AlertTriangle';
  if (title.includes('contact') || title.includes('support')) return 'Mail';
  if (title.includes('cookie') || title.includes('tracking')) return 'Eye';
  if (title.includes('update') || title.includes('change')) return 'Settings';
  if (title.includes('right') || title.includes('legal')) return 'UserCheck';
  if (title.includes('information') || title.includes('collect')) return 'Database';
  if (title.includes('security') || title.includes('protection')) return 'Lock';
  if (title.includes('retention') || title.includes('storage')) return 'Clock';
  if (title.includes('sharing') || title.includes('disclosure')) return 'Globe';
  
  return 'FileText'; // Default icon
};

const legalDocumentUtils = {
  loadLegalDocument,
  parseLegalDocument,
  generateSectionId,
  formatMarkdownContent,
  getSectionIcon
};

export default legalDocumentUtils;
